import React from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DateField } from '@refinedev/mui';
import { Box, Chip, ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StudyData } from '../types';
import { TRANSLATION_KEYS } from '../constants';
import { StudyCategory } from '@aphp-ema/common/study';
import { StudyActionsDropdown } from './StudyActionsDropdown';

const getStudyStatusChipProps = (
  status: StudyData['status'],
): Pick<ChipProps, 'color' | 'variant'> => {
  switch (status) {
    case 'draft':
      return { color: 'primary', variant: 'outlined' };
    case 'published':
      return { color: 'success', variant: 'filled' };
    case 'finished':
      return { color: 'default', variant: 'filled' };
    default:
      return { color: 'primary', variant: 'outlined' };
  }
};

export const useStudyListColumns = (): GridColDef[] => {
  const { t } = useTranslation();

  return React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: t(TRANSLATION_KEYS.FIELDS.NAME),
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'contactName',
        headerName: t(TRANSLATION_KEYS.FIELDS.CONTACT_NAME),
        minWidth: 150,
      },
      {
        field: 'category',
        headerName: t(TRANSLATION_KEYS.FIELDS.CATEGORY),
        minWidth: 150,
        renderCell: (params: GridRenderCellParams<StudyData, StudyCategory>) =>
          params.value?.label || '-',
      },
      {
        field: 'status',
        headerName: t(TRANSLATION_KEYS.FIELDS.STATUS),
        minWidth: 120,
        renderCell: (
          params: GridRenderCellParams<StudyData, StudyData['status']>,
        ) => {
          const chipProps = getStudyStatusChipProps(params.value || 'draft');

          return (
            <Chip
              // @ts-expect-error - TO FIX
              label={t(`${TRANSLATION_KEYS.STATUS.PREFIX}${params.value}`)}
              {...chipProps}
              size="small"
            />
          );
        },
      },
      {
        field: 'startDate',
        headerName: t(TRANSLATION_KEYS.FIELDS.START_DATE),
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<StudyData, string>) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <DateField
              style={{ alignSelf: 'center' }}
              value={params.value}
              format="DD/MM/YYYY"
            />
          </Box>
        ),
      },
      {
        field: 'endDate',
        headerName: t(TRANSLATION_KEYS.FIELDS.END_DATE),
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<StudyData, string>) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <DateField value={params.value} format="DD/MM/YYYY" />
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: t(TRANSLATION_KEYS.TABLE.ACTIONS),
        align: 'right',
        headerAlign: 'right',
        minWidth: 80,
        sortable: false,
        renderCell: (params: GridRenderCellParams<StudyData>) => (
          <StudyActionsDropdown
            studyId={params.row.id}
            studyName={params.row.name || ''}
            studyStatus={params.row.status}
            canEdit={params.row.status === 'draft'}
            canDelete={params.row.status === 'draft'}
          />
        ),
      },
    ],
    [t],
  );
};
